"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useState, useRef, useEffect } from "react";
import { Box, Link } from "@mui/material";
import { SpaceSm, SpaceMd, SpaceXl, SpaceXxs, ColorWhite, SpaceLg, SpaceXs, ColorGray200, CardShadow, ColorCostcoBlue, ColorTextLink } from "@nxt-costco-com/forge-design-tokens";
import { ChevronUp } from "@nxt-costco-com/forge-icons";
import { Text } from "../../DataDisplay/Text/index.js";
import { getDataTestId } from "../../Util/index.js";
const COMPONENT_NAME = "StickyWrapper";
const StickyWrapper = forwardRef(
  ({
    children,
    extraPaddingBottom,
    uniqueId,
    stickyPosition,
    scrollElement,
    width = "100%",
    zIndex,
    position = "sticky",
    hasTopButton,
    isMobile,
    role,
    ariaLabel,
    onScrollToTop,
    showStickyWithTopButton = true,
    sx = {}
  }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const stickyWrapperRef = useRef(null);
    const [stickyWrapperHeight, setStickyWrapperHeight] = useState(0);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const topRef = useRef(null);
    const handleScrollToTop = (event) => {
      event.preventDefault();
      if (topRef.current) {
        topRef.current.focus();
      }
      ((scrollElement == null ? void 0 : scrollElement.current) || window).scrollTo({
        behavior: "smooth",
        top: 0
      });
      if (onScrollToTop) {
        onScrollToTop();
      }
    };
    useEffect(() => {
      const scrollSource = (scrollElement == null ? void 0 : scrollElement.current) || window;
      const handleScroll = () => {
        const scrolled = (scrollElement == null ? void 0 : scrollElement.current) ? scrollElement.current.scrollTop : window.scrollY;
        const threshold = (scrollElement == null ? void 0 : scrollElement.current) ? scrollElement.current.clientHeight : window.innerHeight;
        setShowScrollButton(scrolled > threshold);
      };
      scrollSource.addEventListener("scroll", handleScroll);
      return () => {
        scrollSource.removeEventListener("scroll", handleScroll);
      };
    }, [scrollElement]);
    const topButton = () => {
      return /* @__PURE__ */ jsxs(
        Link,
        {
          ref: topRef,
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
          sx: {
            border: `1px solid ${ColorCostcoBlue}`,
            borderRadius: isMobile ? `${SpaceXxs} 0px 0px ${SpaceXxs}` : SpaceXxs,
            borderRight: isMobile ? 0 : null,
            boxShadow: CardShadow,
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            height: isMobile ? "49px" : "55px",
            width: "40px",
            transition: isMobile ? "right 0.5s" : 0,
            padding: `${SpaceXxs} ${SpaceXs}`,
            backgroundColor: isHovered ? ColorGray200 : ColorWhite,
            minWidth: 0,
            scrollBehavior: "smooth"
          },
          "data-testid": "scroll-to-top-button",
          onClick: handleScrollToTop,
          "aria-label": "Back to Top",
          href: "#",
          underline: "none",
          children: [
            /* @__PURE__ */ jsx(ChevronUp, {}),
            /* @__PURE__ */ jsx(
              Text,
              {
                color: ColorTextLink,
                variant: "t6",
                sx: { lineHeight: "150%", marginTop: "-4px" },
                children: "Top"
              }
            )
          ]
        }
      );
    };
    useEffect(() => {
      var _a;
      if (stickyWrapperRef.current) {
        setStickyWrapperHeight((_a = stickyWrapperRef == null ? void 0 : stickyWrapperRef.current) == null ? void 0 : _a.offsetHeight);
      }
    }, []);
    if (isMobile && hasTopButton) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        showStickyWithTopButton && /* @__PURE__ */ jsx(
          Box,
          {
            "data-testid": getDataTestId(
              COMPONENT_NAME,
              uniqueId
            ),
            role,
            "aria-label": ariaLabel,
            sx: {
              backgroundColor: ColorWhite,
              display: "flex",
              position: "fixed",
              boxShadow: `0px 2px ${SpaceXxs} 0px rgba(0, 0, 0, 0.25)`,
              padding: extraPaddingBottom ? `${SpaceSm} ${SpaceMd} ${SpaceXl} ${SpaceMd}` : `${SpaceSm} ${SpaceMd}`,
              borderRadius: 0,
              border: 0,
              width,
              zIndex,
              justifyContent: "space-between",
              ...sx
            },
            ref: stickyWrapperRef,
            children
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            "data-testid": "top-button",
            sx: {
              position: "fixed",
              right: showScrollButton ? 0 : "-60px",
              zIndex: 1e3,
              transition: showStickyWithTopButton ? "none" : "right 0.5s",
              bottom: stickyWrapperHeight ? `${stickyWrapperHeight + 24}px` : "72px"
              // Use sticky wrapper height dynamically
            },
            children: topButton()
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs(
      Box,
      {
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        role,
        "aria-label": ariaLabel,
        sx: {
          backgroundColor: ColorWhite,
          display: "flex",
          position,
          boxShadow: `0px 2px ${SpaceXxs} 0px rgba(0, 0, 0, 0.25)`,
          [stickyPosition]: 0,
          padding: `${SpaceSm} ${SpaceLg} ${SpaceXs}`,
          borderRadius: `0px 0px ${SpaceXxs} ${SpaceXxs}`,
          border: 0,
          width,
          zIndex,
          justifyContent: "space-between",
          paddingRight: hasTopButton ? "80px" : SpaceLg,
          ...sx
        },
        ref,
        children: [
          children,
          hasTopButton && /* @__PURE__ */ jsx(
            Box,
            {
              sx: {
                right: 0,
                bottom: "72px",
                position: "fixed"
              },
              children: topButton()
            }
          )
        ]
      }
    );
  }
);
StickyWrapper.displayName = COMPONENT_NAME;
export {
  StickyWrapper
};
